import React, { isValidElement, Fragment, useCallback, ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Values substituted into a translation. Plain values are handed to i18next as interpolation
 * options; a React element makes the result a node array instead of a string.
 */
export type ElementsMap = Record<string, ReactNode>;

export interface Translate {
  (text: string): string;
  /** Only elements can split the string into nodes, so a map of primitives stays a string. */
  (text: string, interpolations: Record<string, string | number>): string;
  (text: string, elementsMap: ElementsMap): ReactNode;
}

const useTranslate = () => {
  const { t, i18n } = useTranslation();

  const translate = useCallback(
    (text: string, elementsMap?: ElementsMap): ReactNode => {
      const hasReactElement = elementsMap && Object.values(elementsMap).some(isValidElement);

      if (!i18n.exists(text)) {
        return text;
      }

      if (hasReactElement) {
        const escapedElements = Object.keys(elementsMap).reduce<Record<string, string>>(
          (result, key) => {
            result[key] = `{{${key}}}`;
            return result;
          },
          {}
        );

        const translatedText = t(text, escapedElements);
        const translatedTextParts = translatedText
          .split(/{{(.+?)}}/)
          .filter(textPart => textPart !== "");

        return translatedTextParts.map((part, index) => (
          <Fragment key={`${part}-${index}`}>{elementsMap[part] || part}</Fragment>
        ));
      }

      return t(text, elementsMap);
    },
    [t, i18n]
  );

  // The overloads state a runtime invariant the body cannot prove: with no `elementsMap` there
  // is nothing to substitute, so both remaining paths hand back the string. Only the
  // two-argument form can produce the node array, and only when a value is an element.
  return translate as Translate;
};

export default useTranslate;
